import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import documentService from '../services/documentService';
import DialogModal from './DialogModal';
import { FileText, UploadSimple, DownloadSimple, Trash } from '@phosphor-icons/react';

const CATEGORY_LABELS = {
  contract: 'Contract',
  id_document: 'ID Document',
  certificate: 'Certificate',
  medical: 'Medical',
  academic_record: 'Academic Record',
  other: 'Other',
};

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentsPanel({ ownerType, ownerId, canManage = false }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('other');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }

  const loadDocuments = async () => {
    if (!ownerId) return;
    try {
      setLoading(true);
      const data = await documentService.list(ownerType, ownerId);
      setDocuments(data.documents || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDocuments(); }, [ownerType, ownerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Choose a file first');
      return;
    }
    setUploading(true);
    try {
      await documentService.upload(ownerType, ownerId, category, title, file);
      toast.success('Document uploaded');
      setTitle('');
      setFile(null);
      setFileKey((k) => k + 1);
      loadDocuments();
    } catch (err) {
      toast.error(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await documentService.download(doc.id, doc.original_filename);
    } catch (err) {
      toast.error(err.message || 'Failed to download document');
    }
  };

  const handleDelete = (docId) => {
    setConfirmDialog({
      title: 'Delete Document',
      message: 'Delete this document? This cannot be undone.',
      type: 'error',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        setBusyId(docId);
        try {
          await documentService.remove(docId);
          toast.success('Document deleted');
          loadDocuments();
        } catch (err) {
          toast.error(err.message || 'Failed to delete document');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  return (
    <div className="info-card">
      <h3>Documents</h3>

      {canManage && (
        <form onSubmit={handleUpload} className="form-row" style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
          <div className="form-group" style={{ minWidth: 150 }}>
            <label>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label>Title (optional)</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 2026 Employment Contract" />
          </div>
          <div className="form-group" style={{ minWidth: 200 }}>
            <label>File (PDF, JPEG, PNG — max 10MB)</label>
            <input key={fileKey} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div className="form-group">
            <button className="btn btn-sm btn-primary" type="submit" disabled={uploading}>
              <UploadSimple size={14} /> {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ fontSize: 13, color: '#6b7280' }}>Loading...</p>
      ) : documents.length === 0 ? (
        <p style={{ fontSize: 13, color: '#6b7280' }}>No documents on file.</p>
      ) : (
        <div className="info-rows">
          {documents.map((doc) => (
            <div key={doc.id} className="info-row" style={{ gridTemplateColumns: '1fr auto' }}>
              <span className="value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={18} />
                <span>
                  {doc.title || doc.original_filename}
                  <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 8 }}>
                    {CATEGORY_LABELS[doc.category] || doc.category} · {formatSize(doc.file_size)}
                  </span>
                </span>
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button className="btn-icon" title="Download" onClick={() => handleDownload(doc)}>
                  <DownloadSimple size={18} />
                </button>
                {canManage && (
                  <button className="btn-icon" title="Delete" disabled={busyId === doc.id} onClick={() => handleDelete(doc.id)}>
                    <Trash size={18} />
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

export default DocumentsPanel;
